import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuthContext } from '../../contexts/AuthContext'
import { usePermission } from '../../hooks/usePermissions'
import { comercialContatosPortalService } from '../../services/comercialContatosPortal'
import type { RelatorioPeriodo } from '../../types/comercialContatosPortal'

const MODULE_NAME = 'comercial_contatos_portal'

export type ContatosPortalBrokerSelection = {
  name: string
  id_scgp: number | null
}

/**
 * Filtros compartilhados das abas Contatos Portal + regra de "Ver valores"
 * do próprio módulo (`comercial_contatos_portal`), padrão Cotacoes/Atividades.
 *
 * - Com Ver valores: pode filtrar por qualquer comercial responsável ("Todos" = sem filtro).
 * - Sem Ver valores: select travado no usuário logado (não altera).
 * - Enquanto a permissão carrega: não força comercial (evita filtrar com ID "fantasma" e UI em "Todos").
 * - Corretor: BrokerCombobox padrão Cockpit (SCGP ou nome).
 */
export function useContatosPortalFiltros() {
  const { profile } = useAuthContext()
  const { hasPermission: canViewValues, loading: loadingViewValues } = usePermission(
    MODULE_NAME,
    'view_values'
  )

  const loggedUserId = profile?.id?.trim() ?? ''
  const loggedUserName = profile?.name?.trim() ?? ''
  /** Só restringe depois de saber a permissão — não misturar com `loading`. */
  const isComercialFilterRestricted = !loadingViewValues && !canViewValues

  const [comerciais, setComerciais] = useState<{ id: string; name: string }[]>([])
  const [loadingComercialUsers, setLoadingComercialUsers] = useState(true)
  const [brokerId, setBrokerId] = useState<string | null>(null)
  const [selectedBroker, setSelectedBroker] = useState<ContatosPortalBrokerSelection | null>(null)
  const [comercialUserId, setComercialUserId] = useState('')
  const [periodo, setPeriodo] = useState<RelatorioPeriodo>(
    comercialContatosPortalService.getDefaultRelatorioPeriodo
  )
  const [periodoInvalido, setPeriodoInvalido] = useState(false)

  const displayedComercialUserId = isComercialFilterRestricted ? loggedUserId : comercialUserId
  const effectiveComercialUserId = isComercialFilterRestricted
    ? loggedUserId || '\0'
    : comercialUserId

  const comerciaisOptions = useMemo(() => {
    if (
      isComercialFilterRestricted &&
      loggedUserId &&
      !comerciais.some(u => u.id === loggedUserId)
    ) {
      return [{ id: loggedUserId, name: loggedUserName || 'Usuário logado' }, ...comerciais]
    }
    return comerciais
  }, [comerciais, isComercialFilterRestricted, loggedUserId, loggedUserName])

  useEffect(() => {
    setLoadingComercialUsers(true)
    void comercialContatosPortalService
      .listComercialResponsaveisForFilter()
      .then(users => setComerciais(users.map(u => ({ id: u.id, name: u.nome }))))
      .catch(() => setComerciais([]))
      .finally(() => setLoadingComercialUsers(false))
  }, [])

  useEffect(() => {
    if (loadingViewValues) return
    if (!canViewValues && loggedUserId) {
      setComercialUserId(loggedUserId)
    }
  }, [loadingViewValues, canViewValues, loggedUserId])

  const setCorretor = useCallback(
    (
      id: string | null,
      broker: { id: string; name: string; id_scgp: number | null } | null
    ) => {
      setBrokerId(id)
      setSelectedBroker(broker ? { name: broker.name, id_scgp: broker.id_scgp } : null)
    },
    []
  )

  const validatePeriodo = useCallback(
    (periodoAtual = periodo): string | null =>
      comercialContatosPortalService.validateRelatorioPeriodo(periodoAtual),
    [periodo]
  )

  const resetFiltrosBase = useCallback(() => {
    const defaultPeriodo = comercialContatosPortalService.getDefaultRelatorioPeriodo()
    if (!isComercialFilterRestricted) {
      setComercialUserId('')
    } else if (loggedUserId) {
      setComercialUserId(loggedUserId)
    }
    setBrokerId(null)
    setSelectedBroker(null)
    setPeriodo(defaultPeriodo)
    setPeriodoInvalido(false)
    return {
      periodo: defaultPeriodo,
      brokerId: null as string | null,
      comercialUserId: isComercialFilterRestricted ? loggedUserId || '\0' : '',
    }
  }, [isComercialFilterRestricted, loggedUserId])

  return {
    loggedUserId,
    loggedUserName,
    canViewValues,
    loadingViewValues,
    isComercialFilterRestricted,
    loadingComercialUsers,
    comerciaisOptions,
    brokerId,
    selectedBroker,
    setCorretor,
    comercialUserId,
    setComercialUserId,
    periodo,
    setPeriodo,
    periodoInvalido,
    setPeriodoInvalido,
    displayedComercialUserId,
    effectiveComercialUserId,
    validatePeriodo,
    resetFiltrosBase,
  }
}
